import apiClient from "@/lib/apiClient";
import type { Certificate } from "../types/certificates.types";
import { formatCertificateDate } from "../utils/certificate-pdf-generator";

const ADMIN_CERTIFICATES_ENDPOINT = "/api/admin/certificates";
const ADMIN_EDUCATION_CERTIFICATE_TEMPLATES_ENDPOINT =
  "/api/admin/education-certificate-templates";

const ADMIN_CERTIFICATES_ASSIGN_ENDPOINT = "/api/admin/certificates/assign";
const ADMIN_DOMAIN_SETTINGS_ENDPOINT = "/api/admin/domain-settings";

export enum EducationCertificateTemplateStatus {
  /** Taslak - Draft template, not yet active */
  DRAFT = 0,
  /** Atanmış - Template assigned to at least one education */
  ASSIGNED = 1,
  /** Atanmamış - Template not assigned to any education */
  UNASSIGNED = 2,
  /** Arşiv - Archived template */
  ARCHIVE = 3,
}

/** GET /api/admin/certificates response item (sertifika türleri listesi) */
interface AdminCertificateApiItem {
  id: string;
  name: string | null;
  status: number;
  certificateTemplateID: string;
  description: string | null;
  rawHtml: string;
  createdAt: string;
  updatedAt: string;
  /** İlişkili eğitim varsa backend education.name döner */
  education?: { name?: string };
}

interface AdminCertificatesListResponse {
  success?: boolean;
  status?: number;
  data?: AdminCertificateApiItem[];
  count?: number;
  message?: string;
}

/** GET /api/admin/certificates/{certificateId} response (sertifika detayı) */
export interface CertificateDetailApi {
  id: string;
  name: string | null;
  description: string | null;
  certificateTemplateID: string;
  status: number;
  /** Sertifikayı alan kişi sayısı */
  issuedCount: number;
  /** İlişkili eğitimler — many-to-many, array (null olabilir) */
  education: Array<{ id: string; name: string }> | null;
  createdAt: string;
  updatedAt: string;
  /** Sertifika şablonu HTML (kullanıcı seçilmediğinde gösterilir) */
  rawHtml: string | null;
  certificateNumberPattern: string | null;
}

interface CertificateDetailResponse {
  success?: boolean;
  data?: CertificateDetailApi;
  message?: string;
}

/** GET /api/admin/certificates/{id}/issuedusers response item */
export interface CertificateIssuedUser {
  id: string;
  ad: string;
  soyad: string;
  title: string;
  parsedHtml: string;
  department: { id: string; name: string };
}

interface CertificateIssuedUsersResponse {
  success?: boolean;
  status?: number;
  data?: CertificateIssuedUser[];
  count?: number;
  message?: string;
}

/** Backend'den dönen eğitim sertifika şablonu (GET /api/admin/education-certificate-templates) */
export interface EducationCertificateTemplateApi {
  id?: string;
  name?: string;
  /** Şablon arka plan görseli için indirme URL'i */
  fileDownloadUrl: string;
}

/** Domain settings — sertifika numarası pattern ayarı (Madde 12). */
export interface DomainSettingsDto {
  id: string;
  domainID: string;
  /** null → varsayılan pattern kullanılıyor demektir */
  certificateNumberPattern: string | null;
  /** BE'nin varsayılan pattern'i (null'a sıfırlandığında bu kullanılır) */
  defaultCertificateNumberPattern: string;
  createdAt: string;
  updatedAt: string;
}

interface DomainSettingsResponse {
  success?: boolean;
  data?: DomainSettingsDto;
  message?: string;
}

interface EducationCertificateTemplatesResponse {
  data?: EducationCertificateTemplateApi[];
  success?: boolean;
  message?: string;
}

const STATUS_TO_TAB: Record<number, Certificate["status"]> = {
  0: "draft",
  1: "assigned",
  2: "unassigned",
  3: "archived",
};

function mapApiItemToCertificate(item: AdminCertificateApiItem): Certificate {
  return {
    id: item.id,
    title: item.name ?? item.certificateTemplateID ?? "Sertifika",
    relatedTraining: item.education?.name ?? "",
    issueDate: formatCertificateDate(item.createdAt),
    certificateNo: item.certificateTemplateID,
    rawHtml: item.rawHtml,
    createdAt: item.createdAt,
    status: STATUS_TO_TAB[item.status] ?? "draft",
  };
}

export const certificatesApi = {
  /** GET /api/admin/certificates sortBy alanları (QueryCertificatesAdminDto) */
  sortBy: ["name", "createdAt", "updatedAt", "status"] as const,
  /** GET /api/admin/certificates sortOrder değerleri */
  sortOrder: ["asc", "desc"] as const,

  /**
   * Sertifika türlerini listeler (GET /api/admin/certificates).
   * Query: search (name/description), sortBy (name|createdAt|updatedAt|status), sortOrder (asc|desc).
   * Response: id, name, status, certificateTemplateID, description, rawHtml, createdAt, updatedAt
   */
  getAdminCertificates: async (params: {
    page: number;
    limit: number;
    search?: string;
    status?: EducationCertificateTemplateStatus | number;
    sortBy?: "name" | "createdAt" | "updatedAt" | "status";
    sortOrder?: "asc" | "desc";
  }): Promise<{ data: Certificate[]; total: number }> => {
    const { page, limit, search, status, sortBy, sortOrder } = params;

    const { data: response } = await apiClient.get<AdminCertificatesListResponse>(
      ADMIN_CERTIFICATES_ENDPOINT,
      {
        params: {
          page,
          limit,
          search: search || undefined,
          status,
          sortBy: sortBy || undefined,
          sortOrder: sortOrder || undefined,
        },
      },
    );

    const items = Array.isArray(response?.data) ? response.data : [];
    const total = response?.count ?? items.length;

    return {
      data: items.map(mapApiItemToCertificate),
      total,
    };
  },

  /**
   * Eğitim sertifika şablonlarını listeler (GET /api/admin/education-certificate-templates)
   */
  /**
   * Eğitim sertifika şablonu oluşturur (POST /api/admin/education-certificate-templates).
   * Önce dosya POST /api/admin/files/upload ile yüklenip dönen fileId buraya verilir.
   */
  createEducationCertificateTemplate: async (fileId: string): Promise<void> => {
    await apiClient.post(ADMIN_EDUCATION_CERTIFICATE_TEMPLATES_ENDPOINT, { fileId });
  },

  getEducationCertificateTemplates: async (): Promise<EducationCertificateTemplateApi[]> => {
    // Backend default limit 10 → UI page size 6 ile eşleşmediği için fazla
    // şablonlar fetch edilmiyor (silinen bir şablon hâlâ görünür izlenimi
    // veriyordu). Büyük bir limit gönderip UI'nın kendi pagination'ını
    // doğru veri üzerinde yapmasını sağlıyoruz.
    const { data: response } = await apiClient.get<
      EducationCertificateTemplatesResponse | EducationCertificateTemplateApi[]
    >(ADMIN_EDUCATION_CERTIFICATE_TEMPLATES_ENDPOINT, {
      // Backend DTO limit'i 100'le sınırlı — onun üstünde 400 dönüyor.
      params: { page: 1, limit: 100 },
    });
    if (Array.isArray(response)) return response;
    const items = Array.isArray((response as EducationCertificateTemplatesResponse)?.data)
      ? (response as EducationCertificateTemplatesResponse).data!
      : [];
    return items;
  },

  /**
   * DELETE /api/admin/education-certificate-templates/{templateId}
   * Eğitim sertifika şablonunu soft delete eder. 409 döndüğünde
   * şablondan üretilmiş aktif sertifika var demektir — silinemez.
   */
  deleteEducationCertificateTemplate: async (templateId: string): Promise<void> => {
    await apiClient.delete(
      `${ADMIN_EDUCATION_CERTIFICATE_TEMPLATES_ENDPOINT}/${templateId}`,
    );
  },

  // ─── Madde 12: Sertifika numarası pattern ayarı ───────────────────

  /**
   * GET /api/admin/domain-settings
   * Domain bazlı sertifika numara pattern'i ve varsayılan pattern.
   * Permission: DOMAIN_SETTINGS.VIEW
   */
  getDomainSettings: async (): Promise<DomainSettingsDto> => {
    const { data } = await apiClient.get<DomainSettingsResponse | DomainSettingsDto>(
      ADMIN_DOMAIN_SETTINGS_ENDPOINT,
    );
    const payload = (data as DomainSettingsResponse)?.data ?? (data as DomainSettingsDto);
    return {
      id: payload?.id ?? "",
      domainID: payload?.domainID ?? "",
      certificateNumberPattern: payload?.certificateNumberPattern ?? null,
      defaultCertificateNumberPattern:
        payload?.defaultCertificateNumberPattern ??
        "{YEAR}{MONTH}{DAY}-{SEQ:4}",
      createdAt: payload?.createdAt ?? "",
      updatedAt: payload?.updatedAt ?? "",
    };
  },

  /**
   * PATCH /api/admin/domain-settings
   * Sertifika numarası pattern'i güncelle.
   *   - null / "" → varsayılana döndür
   * Permission: DOMAIN_SETTINGS.UPDATE
   * 400 → "Geçersiz sertifika numarası şablonu..." mesajı
   */
  updateDomainSettings: async (body: {
    certificateNumberPattern?: string | null;
  }): Promise<DomainSettingsDto> => {
    const { data } = await apiClient.patch<DomainSettingsResponse | DomainSettingsDto>(
      ADMIN_DOMAIN_SETTINGS_ENDPOINT,
      body,
    );
    const payload = (data as DomainSettingsResponse)?.data ?? (data as DomainSettingsDto);
    return {
      id: payload?.id ?? "",
      domainID: payload?.domainID ?? "",
      certificateNumberPattern: payload?.certificateNumberPattern ?? null,
      defaultCertificateNumberPattern:
        payload?.defaultCertificateNumberPattern ??
        "{YEAR}{MONTH}{DAY}-{SEQ:4}",
      createdAt: payload?.createdAt ?? "",
      updatedAt: payload?.updatedAt ?? "",
    };
  },

  /**
   * Sertifika detayı (GET /api/admin/certificates/{certificateId}).
   * Alan kişi sayısı, ilişkili eğitim, createdAt.
   */
  getCertificateDetail: async (certificateId: string): Promise<CertificateDetailApi | null> => {
    // 404'ü null'a çeviriyoruz ki silinmiş sertifikada UI crash etmesin; diğer
    // hata kodları (403, 500 vb.) yukarı baloncuk çıksın ki debug edilebilsin.
    try {
      const { data: response } = await apiClient.get<CertificateDetailResponse>(
        `${ADMIN_CERTIFICATES_ENDPOINT}/${certificateId}`
      );
      return response?.data ?? null;
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 404) return null;
      throw err;
    }
  },

  /**
   * Sertifikayı alan kullanıcılar listesi (GET /api/admin/certificates/{certificateId}/issuedusers).
   * Paginated, departmentId ve search ile filtrelenebilir.
   */
  getCertificateIssuedUsers: async (
    certificateId: string,
    params: {
      page: number;
      limit: number;
      departmentId?: string;
      search?: string;
    }
  ): Promise<{ data: CertificateIssuedUser[]; total: number }> => {
    const { page, limit, departmentId, search } = params;
    const { data: response } = await apiClient.get<CertificateIssuedUsersResponse>(
      `${ADMIN_CERTIFICATES_ENDPOINT}/${certificateId}/issuedusers`,
      {
        params: { page, limit, departmentId: departmentId || undefined, search: search || undefined },
      }
    );
    const items = Array.isArray(response?.data) ? response.data : [];
    const total = response?.count ?? items.length;
    return { data: items, total };
  },

  /**
   * Sertifika türü ata (POST /api/admin/certificates/assign).
   * Body: { templateId, rawHtml, name, description, educationId? }
   */
  issueCertificate: async (body: {
    templateId: string;
    rawHtml: string;
    name: string;
    description: string;
    educationId?: string;
    /** Sertifika türü bazında numaralandırma pattern'i (ör. `{YEAR}-{SEQ:4}`). */
    certificateNumberPattern?: string | null;
  }): Promise<void> => {
    await apiClient.post(ADMIN_CERTIFICATES_ASSIGN_ENDPOINT, body);
  },
};

